import {
  ChangeDetectionStrategy,
  Component,
  computed,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  inject,
  input,
  linkedSignal,
  type OnDestroy,
  output,
  signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import type { OdlInputEvent } from '@odilo/ui-components';
import { OdlSelectDirective } from '../../../directives/forms/odl-select-form-accessor';
import { extractWikipediaSummary, resolveWikipediaLinks } from '../../../helpers/wikipedia';
import { extractWiktionaryDefinition, resolveWiktionaryLinks } from '../../../helpers/wiktionary';
import type { TextInfo, Translation } from '../../../models/text-info';
import { ReaderApi } from '../../../services/reader-api/reader-api';
import { TranslatorWatermarkComponent } from '../translator-watermark/translator-watermark';

@Component({
  selector: 'nr-text-info-content',
  imports: [TranslatePipe, ReactiveFormsModule, OdlSelectDirective, TranslatorWatermarkComponent],
  templateUrl: './text-info-content.html',
  styleUrl: './text-info-content.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class TextInfoContentComponent implements OnDestroy {
  private readonly readerApi = inject(ReaderApi);
  private readonly languageMap = new Map<string, string>([
    ['en', 'LANGUAGES.EN'],
    ['es', 'LANGUAGES.ES'],
    ['ca', 'LANGUAGES.CA'],
    ['va', 'LANGUAGES.VA'],
    ['gl', 'LANGUAGES.GL'],
    ['eu', 'LANGUAGES.EU'],
    ['fr', 'LANGUAGES.FR'],
    ['pt', 'LANGUAGES.PT'],
    ['de', 'LANGUAGES.DE'],
    ['zh', 'LANGUAGES.ZH'],
    ['tr', 'LANGUAGES.TR'],
  ]);

  readonly textInfo = input<TextInfo | undefined>();
  readonly initialTranslation = input<Translation | undefined>();
  readonly fallbackText = input.required<string>();
  readonly hasDictionaryInfo = input.required<boolean>();
  readonly isWiktionaryResponse = input.required<boolean>();

  readonly translation = linkedSignal(() => this.initialTranslation());
  readonly wordText = computed(() => this.textInfo()?.word ?? this.fallbackText());
  readonly wiktionaryHtml = computed(() =>
    extractWiktionaryDefinition(
      resolveWiktionaryLinks(this.textInfo()?.wiktionaryResponse?.sections[0]?.text),
    ),
  );
  readonly hasWikipediaInfo = computed(() => Boolean(this.textInfo()?.info));
  readonly wikipediaHtml = computed(() =>
    extractWikipediaSummary(
      resolveWikipediaLinks(this.textInfo()?.info, this.translation()?.detectedSourceLanguage),
    ),
  );
  readonly showMoreInfo = output<'dictionary' | 'wikipedia'>();
  readonly speakingText = signal<string | undefined>(undefined);

  readonly languageOptions = Array.from(this.languageMap.entries()).map(([code, name]) => ({
    code,
    name,
  }));

  readonly originLanguage = computed(() =>
    this.languageMap.get(this.translation()?.detectedSourceLanguage ?? ''),
  );

  readonly targetLanguage = computed(() =>
    this.languageMap.get(this.translation()?.oppositeLanguage ?? ''),
  );

  readonly originLanguageSelector = new FormControl('');
  readonly targetLanguageSelector = new FormControl('');

  constructor() {
    effect(() => {
      this.originLanguageSelector.setValue(this.translation()?.detectedSourceLanguage ?? '', {
        emitEvent: false,
      });
      this.targetLanguageSelector.setValue(this.translation()?.oppositeLanguage ?? '', {
        emitEvent: false,
      });
    });
  }

  async handleTargetLanguageChange(event: OdlInputEvent<string | string[]>): Promise<void> {
    const textToTranslate = this.wordText();
    const oppositeLanguage = event.detail.value;
    if (!textToTranslate || typeof oppositeLanguage !== 'string' || !oppositeLanguage) return;

    const translateResponse = await this.readerApi.getTextTranslation({
      text: textToTranslate,
      targetLanguage: oppositeLanguage,
    });

    this.translation.set({
      ...translateResponse,
      oppositeLanguage: oppositeLanguage,
    });
  }

  async handleOriginLanguageChange(event: OdlInputEvent<string | string[]>): Promise<void> {
    const textToTranslate = this.wordText();
    const sourceLanguage = event.detail.value;
    const oppositeLanguage = this.translation()?.oppositeLanguage;
    if (
      !textToTranslate ||
      typeof sourceLanguage !== 'string' ||
      !sourceLanguage ||
      !oppositeLanguage
    ) {
      return;
    }

    const translateResponse = await this.readerApi.getTextTranslation({
      text: textToTranslate,
      targetLanguage: oppositeLanguage,
      sourceLanguage,
    });

    this.translation.set({
      ...translateResponse,
      oppositeLanguage,
    });
  }

  speak(text: string | undefined, lang: string | undefined): void {
    if (!text || !('speechSynthesis' in window)) return;

    speechSynthesis.cancel();

    if (this.speakingText() === text) {
      this.speakingText.set(undefined);
      return;
    }

    const utterance = new SpeechSynthesisUtterance(text);
    if (lang) {
      utterance.lang = lang;
    }
    utterance.onend = (): void => {
      this.speakingText.set(undefined);
    };
    utterance.onerror = (): void => {
      this.speakingText.set(undefined);
    };
    this.speakingText.set(text);
    speechSynthesis.speak(utterance);
  }

  ngOnDestroy(): void {
    this.speakingText.set(undefined);
    if ('speechSynthesis' in window) {
      speechSynthesis.cancel();
    }
  }
}
